import { ComputingOption } from "@/service/models/request/computation/computing-option";
import { ResponseModelInterfaceStatic } from "@/service/models/response-model-interface";
import { RepositoryCurve } from "@/utilities/graphing/data-repository/curve";
import { DataRepository } from "@/utilities/graphing/data-repository/repository";
import { staticImplements } from "@/utilities/static-implements";
import { DatesRange } from "@/utilities/types/dates-range";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";
import { Point } from "./parts/point";
import { DataResponse } from "./data-response";

@staticImplements<ResponseModelInterfaceStatic>()
export class DataPoints implements DataResponse
{
    public constructor(
        public unit: string,
        public points: Point[],
    )
    {
    }

    /**
     * @inheritdoc
     */
    public static makeFromXml(elem: XmlElementWrapper): DataPoints
    {
        return new this(
            elem.firstChildByTag("unit").value(),
            elem.firstChildByTag("points").mapForEachChildTag("point", (subElem) => Point.makeFromXml(subElem))
        );
    }

    /**
     * @inheritdoc
     */
    public fillDataRepository(
        dataRepository: DataRepository,
        computingParamType: ComputingOption.ParametersType
    ): void
    {
        const dataset = this.getRepositoryDataset(dataRepository, computingParamType);

        if (dataset !== null) {
            this.insertPointsInCurve(dataset);
        }
    }

    /**
     * @inheritdoc
     */
    public fillGraphDataAdjustmentsRepository(dataRepository: DataRepository): void
    {
        this.insertPointsInCurve(dataRepository.selectedAdjustmentPredictionData);
    }

    public getRepositoryDataset(
        dataRepository: DataRepository,
        computingParamType: ComputingOption.ParametersType
    ): RepositoryCurve|null
    {
        switch (computingParamType) {
            case ComputingOption.ParametersType.APRIORI:
                return dataRepository.aprioriPredictionData;

            case ComputingOption.ParametersType.APOSTERIORI:
                return dataRepository.aposterioriPredictionData;

            case ComputingOption.ParametersType.POPULATION:
                return dataRepository.populationPredictionData;

            default:
                return null;
        }
    }

    private insertPointsInCurve(curve: RepositoryCurve): void
    {
        for (const point of this.points) {
            curve.insertDataPoint(point.time, point.value, null);
        }

        if (this.points.length > 0) {
            curve.available.add(new DatesRange(this.points[0].time, this.points[this.points.length - 1].time));
        }
    }
}
